// SPDX-License-Identifier: BUSL-1.1

/**
 * Zod schemas for the 7-kind `ops_agent_activity.payload` shape.
 *
 * Mirrors `PAYLOAD_SCHEMAS` in `packages/core/src/worker/routes/agent-activity.ts`
 * — the server-side ingest validator. The UI types this surface as
 * `Record<string, unknown>` today, which means every render path has to
 * do its own `typeof p.X === 'string'` defensive checks AND new payload
 * fields silently render empty when callers forget to extract them.
 *
 * This module pins the contract in one place:
 *
 *   - Server-side ingest already rejects malformed payloads at the API
 *     boundary. By the time a row hits `ops_agent_activity` the shape
 *     is one of these seven schemas.
 *   - The UI can therefore `safeParse` and trust the parsed type.
 *   - When a new field lands on the server, adding it here surfaces
 *     immediately to every render call-site through the discriminated
 *     union type.
 *
 * If parsing FAILS (network truncated, schema drift between deploys),
 * `parseActivityPayload()` returns `{ kind: 'unknown', raw }` and
 * `renderEventSummary()` falls back to a JSON dump. That's the only
 * silent-failure path — and it's now logged.
 */

import { z } from 'zod';
import type { ActivityEventKind } from './types';

// ---------------------------------------------------------------------------
// Per-kind schemas (mirror server)
// ---------------------------------------------------------------------------

export const toolCallPayloadSchema = z.object({
  tool: z.string().min(1),
  args_summary: z.string().optional(),
  result_size: z.number().int().min(0).optional(),
  ms: z.number().int().min(0).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // NocoDB #4791 — optional job correlation key. Mirrors the server
  // schema in `core/src/worker/routes/agent-activity.ts`. When the
  // emitting runner is inside a job's processing window, it includes
  // `task_id = job.id` so the JobDetailSheet's live feed can filter
  // by `payload.task_id === job.id` (otherwise Zod's default strip
  // behaviour would drop the field before it reaches the UI).
  task_id: z.string().optional(),
});

export const artifactPayloadSchema = z.object({
  type: z.enum(['pr', 'commit', 'doc', 'file']),
  uri: z.string().min(1),
  title: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  task_id: z.string().optional(),
});

export const decisionPayloadSchema = z.object({
  summary: z.string().min(1),
  rationale: z.string().optional(),
  refs: z.array(z.string()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  task_id: z.string().optional(),
});

export const contextCompressedPayloadSchema = z.object({
  before_tokens: z.number().int().min(0),
  after_tokens: z.number().int().min(0),
  ratio: z.number().min(0),
  metadata: z.record(z.string(), z.unknown()).optional(),
  task_id: z.string().optional(),
});

export const blockerPayloadSchema = z.object({
  kind: z.enum(['approval_needed', 'external', 'credentials', 'stall_suspected']),
  detail: z.string().min(1),
  metadata: z.record(z.string(), z.unknown()).optional(),
  task_id: z.string().optional(),
});

export const taskStartedPayloadSchema = z.object({
  task_id: z.string().min(1),
  title: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const taskCompletedPayloadSchema = z.object({
  task_id: z.string().min(1),
  outcome: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Discriminated union
// ---------------------------------------------------------------------------

export type ToolCallPayload = z.infer<typeof toolCallPayloadSchema>;
export type ArtifactPayload = z.infer<typeof artifactPayloadSchema>;
export type DecisionPayload = z.infer<typeof decisionPayloadSchema>;
export type ContextCompressedPayload = z.infer<typeof contextCompressedPayloadSchema>;
export type BlockerPayload = z.infer<typeof blockerPayloadSchema>;
export type TaskStartedPayload = z.infer<typeof taskStartedPayloadSchema>;
export type TaskCompletedPayload = z.infer<typeof taskCompletedPayloadSchema>;

/**
 * Discriminated union of the 7 typed payloads + an `unknown` escape hatch
 * for schema drift / malformed rows. Consumers MUST handle the `unknown`
 * branch explicitly (typescript exhaustiveness check).
 */
export type ParsedActivityPayload =
  | { kind: 'tool_call'; data: ToolCallPayload }
  | { kind: 'artifact'; data: ArtifactPayload }
  | { kind: 'decision'; data: DecisionPayload }
  | { kind: 'context_compressed'; data: ContextCompressedPayload }
  | { kind: 'blocker'; data: BlockerPayload }
  | { kind: 'task_started'; data: TaskStartedPayload }
  | { kind: 'task_completed'; data: TaskCompletedPayload }
  | { kind: 'unknown'; raw: unknown };

const KIND_TO_SCHEMA = {
  tool_call: toolCallPayloadSchema,
  artifact: artifactPayloadSchema,
  decision: decisionPayloadSchema,
  context_compressed: contextCompressedPayloadSchema,
  blocker: blockerPayloadSchema,
  task_started: taskStartedPayloadSchema,
  task_completed: taskCompletedPayloadSchema,
} satisfies Record<ActivityEventKind, z.ZodTypeAny>;

/**
 * Parse a raw activity row's payload against the schema for its `kind`.
 *
 * Returns a discriminated union with the typed payload, or
 * `{ kind: 'unknown', raw }` if parsing fails — that's the schema-drift
 * tripwire. A failure here means either the server is emitting a payload
 * shape the UI doesn't know about (UI deploy lag — surface to ops), or
 * the row was hand-edited (shouldn't happen — flag to ops).
 */
export function parseActivityPayload(
  kind: ActivityEventKind | string,
  raw: unknown,
): ParsedActivityPayload {
  if (!(kind in KIND_TO_SCHEMA)) {
    return { kind: 'unknown', raw };
  }
  const schema = KIND_TO_SCHEMA[kind as ActivityEventKind];
  const result = schema.safeParse(raw);
  if (!result.success) {
    // Drift / malformed — surface in console for ops without crashing UI.
    if (typeof console !== 'undefined') {
      console.warn(
        `[activity-payload] parse failed for kind=${kind}:`,
        result.error.issues,
      );
    }
    return { kind: 'unknown', raw };
  }
  // The cast is safe: KIND_TO_SCHEMA is keyed by ActivityEventKind, so the
  // schema-output type matches the discriminated branch.
  return { kind, data: result.data } as ParsedActivityPayload;
}
